import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Alert,
  Chip,
  Button,
  Popover,
  Divider,
} from '@mui/material';
import { DataTable, ExportButton, DateRangePicker, LoadingSpinner } from '../components/shared';
import { auditService, type AuditLog } from '../services';
import { getApiErrorMessage } from '../lib/apiError';
import { GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';

export const AuditTrailPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [actionFilter, setActionFilter] = useState<string>('');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Detail popover
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  useEffect(() => {
    loadLogs();
  }, [page, pageSize, actionFilter, entityTypeFilter, startDate, endDate]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await auditService.getAuditLogs({
        action: actionFilter || undefined,
        entityType: entityTypeFilter || undefined,
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
        page: page + 1,
        limit: pageSize,
      });

      setLogs(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load audit logs'));
      console.error('Load audit logs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (
    action: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    // Match by keyword within the real action name (e.g. USER_CREATED,
    // LOGIN_FAILED, DHIS2_SYNC_COMPLETED). Ordered so problem/negation keywords
    // win before their positive lookalikes (UNLOCKED before LOCKED,
    // DEACTIVATED before ACTIVATED). First match wins.
    const rules: Array<[string, 'default' | 'primary' | 'error' | 'warning' | 'info' | 'success']> = [
      ['FAILED', 'error'],
      ['DELETED', 'error'],
      ['CANCELLED', 'error'],
      ['UNLOCKED', 'success'],
      ['LOCKED', 'error'],
      ['DEACTIVATED', 'warning'],
      ['DISABLED', 'warning'],
      ['ACTIVATED', 'success'],
      ['ENABLED', 'success'],
      ['CREATED', 'success'],
      ['COMPLETED', 'success'],
      ['SUCCESS', 'success'],
      ['RESENT', 'success'],
      ['SENT', 'success'],
      ['UPDATED', 'info'],
      ['CHANGED', 'info'],
      ['RESET', 'info'],
      ['LOGIN', 'primary'],
      ['LOGOUT', 'default'],
    ];
    const match = rules.find(([keyword]) => action.includes(keyword));
    return match ? match[1] : 'default';
  };

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'Timestamp',
      width: 180,
      valueFormatter: (params) => {
        return params.value ? format(new Date(params.value), 'PPp') : 'N/A';
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getActionColor(params.value)}
        />
      ),
    },
    {
      field: 'entityType',
      headerName: 'Entity Type',
      width: 150,
    },
    {
      field: 'entityId',
      headerName: 'Entity ID',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'userId',
      headerName: 'User ID',
      flex: 1,
      minWidth: 200,
      valueFormatter: (params) => params.value || 'System',
    },
    {
      field: 'ipAddress',
      headerName: 'IP Address',
      width: 140,
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: 'userAgent',
      headerName: 'User Agent',
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
  ];

  // Prepare export data
  const exportData = logs.map((log) => ({
    'Timestamp': log.createdAt ? format(new Date(log.createdAt), 'PPp') : 'N/A',
    'Action': log.action,
    'Entity Type': log.entityType,
    'Entity ID': log.entityId,
    'User ID': log.userId || 'System',
    'IP Address': log.ipAddress || '-',
    'User Agent': log.userAgent || '-',
    'Changes': log.details ? JSON.stringify(log.details) : '-',
  }));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Audit Trail
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename={`audit-trail-${format(new Date(), 'yyyy-MM-dd')}`}
          disabled={logs.length === 0}
          variant="contained"
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'center' }}>
          <TextField
            select
            label="Action"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            sx={{ minWidth: 150, flex: { xs: '1 1 100%', md: '1 1 auto' } }}
            size="small"
          >
            <MenuItem value="">All Actions</MenuItem>
            <MenuItem value="CREATE">Create</MenuItem>
            <MenuItem value="UPDATE">Update</MenuItem>
            <MenuItem value="DELETE">Delete</MenuItem>
            <MenuItem value="LOGIN">Login</MenuItem>
            <MenuItem value="LOGOUT">Logout</MenuItem>
            <MenuItem value="VIEW">View</MenuItem>
            <MenuItem value="SYNC">Sync</MenuItem>
          </TextField>

          <TextField
            select
            label="Entity Type"
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            sx={{ minWidth: 180, flex: { xs: '1 1 100%', md: '1 1 auto' } }}
            size="small"
          >
            <MenuItem value="">All Entity Types</MenuItem>
            <MenuItem value="User">User</MenuItem>
            <MenuItem value="Facility">Facility</MenuItem>
            <MenuItem value="Recipient">Recipient</MenuItem>
            <MenuItem value="Reminder">Reminder</MenuItem>
            <MenuItem value="SmsLog">SMS Log</MenuItem>
          </TextField>

          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startLabel="Start Date"
            endLabel="End Date"
          />

          {(actionFilter || entityTypeFilter || startDate || endDate) && (
            <Button
              size="small"
              onClick={() => { setActionFilter(''); setEntityTypeFilter(''); setStartDate(null); setEndDate(null); }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      {/* Data Table */}
      {loading && logs.length === 0 ? (
        <LoadingSpinner message="Loading audit logs..." />
      ) : logs.length === 0 ? (
        <Paper
          sx={{
            p: 8,
            textAlign: 'center',
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="h6" gutterBottom color="text.secondary">
            No Audit Logs Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {actionFilter || entityTypeFilter || startDate || endDate
              ? 'Try adjusting your filters'
              : 'Audit logs will appear here as users perform actions in the system'}
          </Typography>
        </Paper>
      ) : (
        <DataTable
          rows={logs}
          columns={columns}
          loading={loading}
          rowCount={total}
          paginationMode="server"
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          autoHeight
          onRowClick={(params, event) => {
            setSelectedLog(params.row as AuditLog);
            setPopoverAnchor(event.currentTarget as HTMLElement);
          }}
        />
      )}

      {/* Detail Popover */}
      <Popover
        open={!!popoverAnchor}
        anchorEl={popoverAnchor}
        onClose={() => { setPopoverAnchor(null); setSelectedLog(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { maxWidth: 450, width: '100%' } } }}
      >
        {selectedLog && (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>Audit Detail</Typography>
              <Chip label={selectedLog.action} size="small" color={getActionColor(selectedLog.action)} />
            </Box>
            <Divider sx={{ mb: 1.5 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                <Typography variant="body2">{format(new Date(selectedLog.createdAt), 'PPp')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Entity Type</Typography>
                <Typography variant="body2" fontWeight={500}>{selectedLog.entityType || '-'}</Typography>
              </Box>
              {selectedLog.entityId && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Entity ID</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{selectedLog.entityId}</Typography>
                </Box>
              )}
              {selectedLog.user && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">User</Typography>
                  <Typography variant="body2" fontWeight={500}>{selectedLog.user.firstName} {selectedLog.user.lastName}</Typography>
                </Box>
              )}
              {selectedLog.ipAddress && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">IP Address</Typography>
                  <Typography variant="body2">{selectedLog.ipAddress}</Typography>
                </Box>
              )}
            </Box>

            {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">Details</Typography>
                <Box sx={{ mt: 0.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </Box>
              </>
            )}

            {selectedLog.userAgent && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">User Agent</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary', wordBreak: 'break-all' }}>
                  {selectedLog.userAgent}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
};
